import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ethers } from 'ethers';
import { useFactoryAddresses } from '../hooks/useFactoryAddresses';
import { USER_MANAGER_ABI } from '../types/contracts';
import { makeContractCall, randomDelay, makeSequentialContractCalls } from '../utils/contractUtils';

interface UserManagerProps {
  onUserAdded?: (address: string) => void;
  onUserRemoved?: (address: string) => void;
}

const UserManager: React.FC<UserManagerProps> = ({ onUserAdded, onUserRemoved }) => {
  const [newUserAddress, setNewUserAddress] = useState('');
  const [contractManagerAddress, setContractManagerAddress] = useState('');
  const [allowedUsers, setAllowedUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [userStatus, setUserStatus] = useState<{ [key: string]: boolean }>({});
  const [contractManagers, setContractManagers] = useState<{ [key: string]: string }>({});

  const { factoryAddresses, loading: factoryLoading, error: factoryError } = useFactoryAddresses();

  // Use the imported ABI
  const userManagerABI = useMemo(() => USER_MANAGER_ABI, []);


  const loadAllowedUsers = useCallback(async () => {
    if (!factoryAddresses?.factories.userManager) {
      console.log('No UserManager address found');
      return;
    }

    // Check if MetaMask is available
    if (!window.ethereum) {
      setError('MetaMask is not installed. Please install MetaMask to interact with contracts.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('Loading allowed users from:', factoryAddresses.factories.userManager);

      // Use injected wallet provider
      const provider = new ethers.BrowserProvider(window.ethereum);
      
      const userManagerContract = new ethers.Contract(
        factoryAddresses.factories.userManager,
        userManagerABI,
        provider
      );

      console.log('Contract instance created, calling getAllowedUsersCount...');
      
      // First check if there are any users
      const userCount = await makeContractCall(
        () => userManagerContract.getAllowedUsersCount(),
        'get allowed users count'
      );
      console.log('User count:', userCount.toString());
      
      if (userCount > 0) {
        console.log('Getting allowed users list...');
        const users = await makeContractCall(
          () => userManagerContract.getAllowedUsers(),
          'get all allowed users'
        );
        console.log('Users retrieved:', users);
        setAllowedUsers(users);

        // Load contract managers for each user sequentially to avoid rate limiting
        const managerMap: { [key: string]: string } = {};
        
        if (users.length > 0) {
          console.log(`Loading contract managers for ${users.length} users...`);
          
          const managerCalls = users.map((user: string) => 
            () => userManagerContract.contractManagerForUser(user)
          );
          const managerCallNames = users.map((user: string) => 
            `get contract manager for user ${user}`
          );
          
          try {
            const managers = await makeSequentialContractCalls(managerCalls, managerCallNames);
            users.forEach((user: string, index: number) => {
              managerMap[user] = managers[index];
            });
          } catch (err) {
            console.error('Error loading contract managers:', err);
            // Fallback: set all to Unknown
            users.forEach((user: string) => {
              managerMap[user] = 'Unknown';
            });
          }
        }
        
        setContractManagers(managerMap);
      } else {
        console.log('No users found, setting empty array');
        setAllowedUsers([]);
        setContractManagers({});
      }
    } catch (err) {
      console.error('Error loading allowed users:', err);
      setAllowedUsers([]);
      setContractManagers({});
    } finally {
      setLoading(false);
    }
  }, [factoryAddresses, userManagerABI]);

  // Load allowed users on component mount
  useEffect(() => {
    if (factoryAddresses?.factories.userManager) {
      console.log('Factory addresses loaded:', factoryAddresses);
      loadAllowedUsers();
    }
  }, [factoryAddresses, loadAllowedUsers]);

  const checkUserStatus = async (address: string) => {
    if (!factoryAddresses?.factories.userManager || !ethers.isAddress(address)) {
      console.log('Invalid address or no UserManager:', address);
      return;
    }

    try {
      setIsCheckingUser(true);
      console.log('Checking status for address:', address);
      
      // Use injected wallet provider if available, otherwise fallback to Base public RPC
      let provider;
      if (window.ethereum) {
        provider = new ethers.BrowserProvider(window.ethereum);
      } else {
        // Use Base public RPC as fallback
        provider = new ethers.JsonRpcProvider("https://mainnet.base.org");
      }
      
      const userManagerContract = new ethers.Contract(
        factoryAddresses.factories.userManager,
        userManagerABI,
        provider
      );

      const isAllowed = await makeContractCall(
        () => userManagerContract.isUserAllowed(address),
        `check if user ${address} is allowed`
      );
      console.log('User allowed status:', isAllowed);
      setUserStatus(prev => ({ ...prev, [address]: isAllowed }));
    } catch (err) {
      console.error('Error checking user status:', err);
    } finally {
      setIsCheckingUser(false);
    }
  };

  const addUser = async () => {
    if (!factoryAddresses?.factories.userManager) {
      return;
    }

    if (!newUserAddress.trim() || !contractManagerAddress.trim()) {
      setError('Please enter both user address and contract manager address');
      return;
    }

    if (!ethers.isAddress(newUserAddress) || !ethers.isAddress(contractManagerAddress)) {
      setError('Please enter valid Ethereum addresses');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      console.log('Adding user:', newUserAddress, 'with contract manager:', contractManagerAddress);
      console.log('Using UserManager at:', factoryAddresses.factories.userManager);

      // Check if MetaMask is available for transactions
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed. Please install MetaMask to send transactions.');
      }

      // Request account access
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      
      // Use injected wallet provider for transactions
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      console.log('Signer address:', await signer.getAddress());
      
      const userManagerContract = new ethers.Contract(
        factoryAddresses.factories.userManager,
        userManagerABI,
        signer
      );

      console.log('Calling manageUser with:', newUserAddress, true, contractManagerAddress);
      const tx = await userManagerContract.manageUser(newUserAddress, true, contractManagerAddress);
      console.log('Transaction sent:', tx.hash);
      
      const receipt = await tx.wait();
      console.log('Transaction confirmed:', receipt);

      setSuccess(`User ${newUserAddress} has been added successfully with contract manager ${contractManagerAddress}!`);
      setNewUserAddress('');
      setContractManagerAddress('');
      await loadAllowedUsers();
      onUserAdded?.(newUserAddress);
    } catch (err) {
      console.error('Error adding user:', err);
      setError(`Failed to add user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const removeUser = async (address: string) => {
    if (!factoryAddresses?.factories.userManager) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Check if MetaMask is available for transactions
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed. Please install MetaMask to send transactions.');
      }

      // Request account access
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      
      // Use injected wallet provider for transactions
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      
      const userManagerContract = new ethers.Contract(
        factoryAddresses.factories.userManager,
        userManagerABI,
        signer
      );

      const tx = await userManagerContract.removeUser(address);
      await tx.wait();

      setSuccess(`User ${address} has been removed successfully!`);
      await loadAllowedUsers();
      onUserRemoved?.(address);
    } catch (err) {
      console.error('Error removing user:', err);
      setError(`Failed to remove user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };


  const addCurrentUser = async () => {
    if (!factoryAddresses?.deployer) {
      return;
    }

    setNewUserAddress(factoryAddresses.deployer);
    setContractManagerAddress(factoryAddresses.deployer); // Use deployer as contract manager by default
    await addUser();
  };


  if (factoryLoading) {
    return (
      <div className="user-manager">
        <h2>👥 User Manager</h2>
        <p>Loading factory addresses...</p>
      </div>
    );
  }

  if (factoryError) {
    return (
      <div className="user-manager">
        <h2>👥 User Manager</h2>
        <p>Error loading factory addresses: {factoryError}</p>
      </div>
    );
  }

  if (!factoryAddresses?.factories.userManager) {
    return (
      <div className="user-manager">
        <h2>👥 User Manager</h2>
        <p>UserManager contract not found. Please deploy the factory contracts first.</p>
      </div>
    );
  }

  return (
    <div className="user-manager">
      
      
      {success && (
        <div className="success-message">
          <p>✅ {success}</p>
        </div>
      )}

      <div className="add-user-section">
        <h3>Add New User</h3>
        <div className="input-group">
          <input
            type="text"
            placeholder="Enter user Ethereum address (0x...)"
            value={newUserAddress}
            onChange={(e) => setNewUserAddress(e.target.value)}
            disabled={loading}
          />
          <input
            type="text"
            placeholder="Enter contract manager address (0x...)"
            value={contractManagerAddress}
            onChange={(e) => setContractManagerAddress(e.target.value)}
            disabled={loading}
          />
          <button 
            onClick={addUser} 
            disabled={loading || !newUserAddress.trim() || !contractManagerAddress.trim()}
            className="add-button"
          >
            {loading ? 'Adding...' : 'Add User'}
          </button>
        </div>
        
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button 
            onClick={addCurrentUser} 
            disabled={loading}
            className="add-button"
            style={{ backgroundColor: '#000000', borderColor: '#ffff00', color: '#ffff00' }}
          >
            Add Current Deployer ({factoryAddresses.deployer})
          </button>
        </div>
        
        {newUserAddress && ethers.isAddress(newUserAddress) && (
          <div className="user-status-check">
            <button 
              onClick={() => checkUserStatus(newUserAddress)}
              disabled={isCheckingUser}
              className="check-button"
            >
              {isCheckingUser ? 'Checking...' : 'Check Status'}
            </button>
            {userStatus[newUserAddress] !== undefined && (
              <span className={`status ${userStatus[newUserAddress] ? 'allowed' : 'not-allowed'}`}>
                {userStatus[newUserAddress] ? '✅ Allowed' : '❌ Not Allowed'}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="allowed-users-section">
        <div className="section-header">
          <h3>Allowed Users ({allowedUsers.length})</h3>
          <button 
            onClick={loadAllowedUsers} 
            disabled={loading}
            className="refresh-button"
          >
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>
        
        {loading && allowedUsers.length === 0 ? (
          <p>Loading allowed users...</p>
        ) : allowedUsers.length === 0 ? (
          <div>
            <p>No users are currently allowed.</p>
            <p style={{ color: '#ffff00', fontSize: '0.8rem' }}>
              ⚠️ You need to add users before they can create contracts through the factories.
            </p>
          </div>
        ) : (
          <div className="users-list">
            {allowedUsers.map((address, index) => (
              <div key={address} className="user-item">
                <div className="user-info">
                  <span className="user-index">#{index + 1}</span>
                  <div className="user-details">
                    <span className="user-address">{address}</span>
                    <br />
                    <span className="contract-manager">
                      Manager: {contractManagers[address] || 'Loading...'}
                    </span>
                  </div>
                </div>
                <button 
                  onClick={() => removeUser(address)}
                  disabled={loading}
                  className="remove-button"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>


      <div className="contract-info">
        <h3>Contract Information</h3>
        <p><strong>UserManager Address:</strong> {factoryAddresses.factories.userManager}</p>
        <p><strong>Network:</strong> {factoryAddresses.network} (Chain ID: {factoryAddresses.chainId})</p>
        <p><strong>Deployer:</strong> {factoryAddresses.deployer}</p>
        <p><strong>Current Users Count:</strong> {allowedUsers.length}</p>
        <p><strong>Loading State:</strong> {loading ? 'Yes' : 'No'}</p>
        <p><strong>Error State:</strong> {error || 'None'}</p>
      </div>
    </div>
  );
};

export default UserManager;
